import {
  Component, Inject, OnDestroy, OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  startWith, takeUntil
} from 'rxjs';

import { WizardService } from '@sondermind/flows-host';
import {
  FlowsLaunchDarklyService,
  LAUNCH_DARKLY_SERVICE,
  UserType
} from '@sondermind/launch-darkly';
import { ResponseData, ResponseUpdateItem } from '@sondermind/utilities/models-flows';

import { IGeoLocationForm, IGeoLocation } from '../basic-form/model';
import { IntakeStepComponent } from '../intake-step.component';

export type FormLocation = {
  name: string;
  lat: number;
  lng: number;
  address: string;
};

interface GeoLocationForm {
  name: FormControl<string>;
  lat: FormControl<number>;
  lng: FormControl<number>;
  address: FormControl<string>;
}

@Component({
  selector: 'flows-intake-geolocation',
  templateUrl: './intake-geolocation.component.html',
  styleUrls: ['./intake-geolocation.component.scss']
})
export class IntakeGeoLocation extends IntakeStepComponent<IGeoLocation> implements OnInit, OnDestroy {
  constructor(
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: FlowsLaunchDarklyService,
    wizard: WizardService
  ) {
    super(wizard);
  }

  formGroup: FormGroup<GeoLocationForm> = new FormGroup<GeoLocationForm>({
    name: new FormControl(null),
    lat: new FormControl(null, Validators.required),
    lng: new FormControl(null, Validators.required),
    address: new FormControl(null, Validators.required),
  });

  addressFormControl = new FormControl(null, Validators.required);
  placeholder: string = '';
  formItem: IGeoLocationForm;
  value: ResponseData;
  state: string;

  ngOnInit(): void {
    [this.formItem] = this.config.form;
    this.value = { ...this.data };

    this.initFormValues();
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  subscribeToFormChanges(): void {
    this.addressFormControl.valueChanges.pipe(
      startWith(this.addressFormControl.value),
      takeUntil(this.destroyed$)
    ).subscribe((newAddress) => {
      // if current value and saved form address don't match, clear out value from manual address change
      const isManualAddressChange = this.formGroup.valid && newAddress !== this.formGroup.value?.address;
      if (isManualAddressChange) {
        this.formGroup.reset();
        this.valid.next(false);
      } else {
        this.valid.next(this.formGroup.valid);
      }
    });
  }

  initFormValues(): void {
    const location: FormLocation = this.value[this.formItem?.name];
    if (!location) return;

    this.formGroup.patchValue({ ...location });
    this.addressFormControl.patchValue(location.address);
  }

  locationSelected(selectedAddress: google.maps.places.PlaceResult): void {
    if (!(selectedAddress?.geometry && selectedAddress?.formatted_address)) return;

    // administrative_area_level_1 always corresponds to a state in the US, see
    // https://developers.google.com/maps/documentation/geocoding/requests-geocoding#Type
    this.state = selectedAddress.address_components.find(
      (x) => x.types.includes('administrative_area_level_1'))?.long_name;

    const location: FormLocation = {
      name: selectedAddress.name,
      lat: selectedAddress.geometry.location.lat(),
      lng: selectedAddress.geometry.location.lng(),
      address: selectedAddress.formatted_address
    };

    this.formGroup.patchValue({ ...location });
    this.addressFormControl.patchValue(location.address);
  }

  onSubmit(): ResponseUpdateItem[] {
    if (this.formItem.type === 'geolocation' && this.state) {
      const updatedUser = {
        key: this.launchDarklyService.uuid,
        custom: {
          userType: UserType.CLIENT,
          state: this.state // Update the state in the user attributes
        }
      };
      this.launchDarklyService.update(updatedUser);
    }

    return [
      {
        human: this.formItem.label || this.formItem.name,
        key: this.formItem.name,
        value: this.formGroup.value
      }
    ];
  }
}
